/* eslint-disable prettier/prettier */
import { useQuery } from "@apollo/client";
import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import gql from "graphql-tag";
import { ExternalLink } from "../../../../../shared/components/design-system/Button/Link";
import { useToast } from "../../../../../shared/components/design-system/Toaster/context";
import styles from "./styles.module.scss";

const REDACTED_REPORTS_URL = "/reports/all?redaction-status=REDACTED";
const FAILED_REDACTIONS_URL = "/reports/all?redaction-status=FAILED";

type BulkRedactionJobMonitorQueryVariables = {
  id: string;
};

type BulkRedactionJobMonitorQuery = {
  bulkRedactionJob: {
    id: string;
    status: string;
    completedReports: number;
    failedReports: number;
    totalReports: number;
  };
};

const BULK_REDACTION_JOB_QUERY = gql`
  query bulkRedactionJobMonitor($id: ID!) {
    bulkRedactionJob(id: $id) {
      id
      status
      completedReports
      failedReports
      totalReports
    }
  }
`;

type BulkRedactionJobMonitorContextValue = {
  trackBulkRedactionJob: (jobId: string) => void;
};

const BulkRedactionJobMonitorContext = createContext<BulkRedactionJobMonitorContextValue | null>(null);

export function useBulkRedactionJobMonitor() {
  const context = useContext(BulkRedactionJobMonitorContext);

  if (!context) {
    throw new Error("useBulkRedactionJobMonitor must be used within BulkRedactionJobMonitorProvider");
  }

  return context;
}

export function BulkRedactionJobMonitorProvider({ children }: PropsWithChildren<unknown>) {
  const [jobIds, setJobIds] = useState<string[]>([]);

  const trackBulkRedactionJob = useCallback((jobId: string) => {
    setJobIds((jobIds) => (jobIds.includes(jobId) ? jobIds : [...jobIds, jobId]));
  }, []);

  const clearBulkRedactionJob = useCallback((jobId: string) => {
    setJobIds((jobIds) => jobIds.filter((trackedJobId) => trackedJobId !== jobId));
  }, []);

  const context = useMemo(() => ({ trackBulkRedactionJob }), [trackBulkRedactionJob]);

  return (
    <BulkRedactionJobMonitorContext.Provider value={context}>
      <BulkRedactionJobMonitor jobId={jobIds[0] ?? null} onComplete={clearBulkRedactionJob} />
      {children}
    </BulkRedactionJobMonitorContext.Provider>
  );
}

function BulkRedactionJobMonitor({ jobId, onComplete }: { jobId: string | null; onComplete: (jobId: string) => void }) {
  const toast = useToast();
  const { data, stopPolling } = useQuery<BulkRedactionJobMonitorQuery, BulkRedactionJobMonitorQueryVariables>(
  BULK_REDACTION_JOB_QUERY,
  {
    variables: { id: jobId ?? "" },
    skip: !jobId,
    pollInterval: jobId ? 3000 : 0,
    fetchPolicy: "network-only",
  },
);

  useEffect(() => {
    const job = data?.bulkRedactionJob;

    if (!jobId || !job) return;

    if (job.status === "COMPLETED") {
      toast.success(
        <>
          Bulk redaction completed. {job.completedReports} reports successfully redacted.{" "}
          <ExternalLink to={REDACTED_REPORTS_URL} suppressIcon className={styles.ToastLink}>
            View all redacted reports
          </ExternalLink>
        </>,
        { persistent: true },
      );

      stopPolling();
      onComplete(jobId);
    }

    if (job.status === "PARTIALLY_FAILED") {
      toast.warning(
        <>
          Bulk redaction partially completed. {job.completedReports} successfully redacted, {job.failedReports} failed
          to process.{" "}
          <ExternalLink to={REDACTED_REPORTS_URL} suppressIcon className={styles.ToastLink}>
            View all redacted reports
          </ExternalLink>{" "}
          or{" "}
          <ExternalLink to={FAILED_REDACTIONS_URL} suppressIcon className={styles.ToastLink}>
            view currently failed redactions
          </ExternalLink>
        </>,
        { persistent: true },
      );

      stopPolling();
      onComplete(jobId);
    }

    if (job.status === "FAILED") {
      toast.danger(
        <>
          Bulk redaction failed completely. {job.failedReports} reports failed to process.{" "}
          <ExternalLink to={FAILED_REDACTIONS_URL} className={styles.ToastLink} suppressIcon>
            View currently failed redactions
          </ExternalLink>
        </>,
        { persistent: true },
      );

      stopPolling();
      onComplete(jobId);
    }
  }, [data, jobId, onComplete, stopPolling, toast]);

  return null;
}
